/**
 * Created by miguel on 16/3/17.
 */

interface InvoiceLineDelegate 
{
    lineDidInserted( line:InvoiceLine) : void;
    lineDidRemove( line:InvoiceLine)   : void;
    lineDidUpdate( line:InvoiceLine)   : void;

    lineSectionDidInsert( line:DBDocumentTitleLine) : void;
}

class InvoiceLineCell extends UITableViewCell
{    
    delegate:InvoiceLineDelegate = null;

    protected addButton:MUIButton = null;
    protected productTextField:MUITextField = null;
    protected productLabel:MUILabel = null;
    protected conceptTextRow:MUIView = null;
    protected printedRow:MUIView = null;
    protected conceptLabel:MUILabel = null;
    protected infoButton:MUIButton = null;
    protected infoPrintedButton:MUIButton = null;
    protected taxDropDown:MUIButton = null;
    protected taxLabel:MUILabel = null;
    protected legalTaxDropDown:MUIButton = null;
    protected legalTaxLabel:MUILabel = null;
    protected classificationDropdown:MUIButton = null;
    protected classificationLabel:MUILabel = null;
    protected quantityLabel:MUILabel = null;
    protected quantityTextField:MUITextField = null;
    protected unitPriceLabel:MUILabel = null;    
    protected unitPriceTextField:MUITextField = null;    
    protected discountLabel:MUILabel = null;
    protected discountTextField:MUITextField = null;    
    protected totalLabel:MUILabel = null;
    protected totalTextField:MUITextField = null;
    
    protected cf = MUIWebApplication.sharedInstance().delegate.currencyFormatter;
    protected nf = MUIWebApplication.sharedInstance().delegate.numberFormatter;
    protected pnf = MUIWebApplication.sharedInstance().delegate.percentNumberFormatter;

    protected product:Product|null = null;
    protected conceptItem:ConceptItem|null = null;
    protected vat:Tax|null = null;
    protected vat_factor:number = 0;
    protected legal_tax:LegalTax|null = null;
    protected legal_tax_factor:number = 0;
    protected classification:DBDocumentLineClassification|null = null;

    protected quantity:number = 0;
    protected price_amount:number = 0;
    protected base_amount:number = 0;
    protected discount_format:string|null = null;
    protected discount_amount:number = 0;
    protected vat_amount:number = 0;
    protected legal_tax_amount:number = 0;
    protected total_amount:number = 0;

    // Locked status for calculation priority (bitwise flags)
    // Uses LockedStatus enum from StockNoteLine: none=0, price=1, productPrice=2, discount=4, total=8
    protected lockStatus:LockedStatus = LockedStatus.none;

    awakeFromHTML()
    {
        this.addButton = MUIOutlet(this, 'add-line-btn', 'MUIButton');
        if (this.addButton) this.addButton.setAction(this, this.addButtonDidPress);
        this.productTextField = MUIOutlet(this, 'product-tf', 'MUITextField');
        this.setupProductTextField(this.productTextField, this, this.itemDidSelect);
        this.productLabel = MUIOutlet(this, 'product-lbl', 'MUILabel');
        this.conceptTextRow = MUIOutlet(this, "concept-text-row", "MUIView");
        this.printedRow = MUIOutlet(this, "printed-row", "MUIView");
        this.conceptLabel = MUIOutlet(this, "concept-lbl", "MUILabel");
        this.infoButton = MUIOutlet( this, "info-icon-btn", "MUIButton" );
        this.infoButton?.setAction( this, this.showInfoAlert );
        this.infoPrintedButton = MUIOutlet( this, "info-printed-btn", "MUIButton" );
        this.infoPrintedButton?.setAction( this, this.showInfoAlert );
        this.taxLabel = MUIOutlet(this, "tax-lbl", "MUILabel");
        this.taxDropDown = MUIOutlet(this, 'tax-dd','MUIButton');
        this.setupTaxDropdown(this.taxDropDown);               
        this.legalTaxLabel = MUIOutlet(this, "legal-tax-lbl", "MUILabel");
        this.legalTaxDropDown = MUIOutlet(this, 'legal-tax-dd','MUIButton');
        this.setupLegalTaxDropdown(this.legalTaxDropDown);
        this.classificationLabel = MUIOutlet(this, 'classification-lbl', 'MUILabel');
        this.classificationDropdown = MUIOutlet(this, 'classification-dd', 'MUIButton');
        this.setupClassificationDropdown(this.classificationDropdown);
        this.quantityLabel = MUIOutlet(this, 'quantity-lbl','MUILabel');
        this.quantityTextField = MUIOutlet(this, 'quantity-tf', "MUITextField");
        this.setupQuantityTextField(this.quantityTextField);
        this.unitPriceLabel = MUIOutlet(this, 'unit-price-lbl','MUILabel');
        this.unitPriceTextField = MUIOutlet(this, 'unit-price-tf', "MUITextField");
        this.setupUnitPriceTextField(this.unitPriceTextField);        
        this.discountLabel = MUIOutlet(this, 'discount-lbl','MUILabel');
        this.discountTextField = MUIOutlet(this, 'discount-tf','MUITextField');
        this.setupDiscountTextField(this.discountTextField);        
        this.totalLabel = MUIOutlet(this, 'total-lbl','MUILabel');
        this.totalTextField = MUIOutlet(this, 'total-tf', "MUITextField");
        this.setupTotalTextField(this.totalTextField);

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    invoice:Invoice = null;

    /// The retention the customer or the supplier of the invoice has in its info: a new line
    /// starts with it, an entity without one starts with none. Another one, or none, can still
    /// be picked on the line. Only the cell that adds lines takes it; a line of the document
    /// shows its own.
    private _defaultLegalTax:LegalTax|null = null;
    set defaultLegalTax(tax:LegalTax|null){
        // the same one again (the view refreshing) leaves what was picked for the line being written
        if (this._defaultLegalTax === tax) return;
        this._defaultLegalTax = tax;
        if (this.line == null) this.applyDefaultLegalTax();
    }

    private applyDefaultLegalTax(){
        let tax = this._defaultLegalTax;
        this.legal_tax = tax;
        this.legal_tax_factor = tax != null ? tax.value : 0;
        this.setDropdownTitle(this.legalTaxDropDown, tax?.name, MIOLocalizeString("BUSINESS_TAX", "BUSINESS TAX"));
    }

    /// The invoice is under review (its document is being read, or the reading is being gone
    /// through): the lines show the product link field with the printed text under it. Otherwise
    /// a line reads as text, as the lines always did.
    underReview:boolean = false;

    protected line:DBDocumentLine = null;
    // A line of the document, or a detected line to add, is flagged in red while it has no VAT;
    // the new line at the bottom is not.
    protected strictVat:boolean = false;

    set item(i:DBDocumentLine)
    {
        this.line = i;
        this.strictVat = true;

        if (this.conceptLabel) this.conceptLabel.text = this.conceptString();
        this.quantity = i.quantity;
        if (this.quantityLabel) this.quantityLabel.text = this.nf.stringFromNumber(i.quantity);
        if (this.quantityTextField) this.quantityTextField.text = this.nf.stringFromNumber(i.quantity);

        this.classification = i.lineClassification;
        this.setDropdownTitle(this.classificationDropdown, i.lineClassification?.title, MIOLocalizeString("CHOOSE", "CHOOSE"));
        if (this.classificationLabel) this.classificationLabel.text = i.lineClassification?.title;

        if (i instanceof InvoiceLine) {

            this.product = i.product;
            this.conceptItem = i.conceptItem;
            this.vat = i.vat;
            this.vat_factor = i.vatFactor;
            this.legal_tax = i.legalTax;
            this.legal_tax_factor = i.legalTaxFactor;        
            this.price_amount = i.priceAmount;
            this.discount_format = i.discountFormat;
            this.discount_amount = i.discountAmount;
            this.base_amount = i.baseAmount;
            this.total_amount = i.totalAmount;

                
            if (this.unitPriceLabel) this.unitPriceLabel.text = this.cf.stringFromNumber(i.priceAmount);
            if (this.unitPriceTextField) this.unitPriceTextField.text = this.cf.stringFromNumber(i.priceAmount);
            if (this.taxLabel) this.taxLabel.text = i.vat?.name;
            this.setDropdownTitle(this.legalTaxDropDown, i.legalTax?.name, MIOLocalizeString("NONE", "NONE"));
            if (this.legalTaxLabel) this.legalTaxLabel.text = i.legalTax?.name;
            if (this.discountLabel) {
                if (i.discountFormat != null) this.discountLabel.text = i.discountFormat;
                else if (i.discountAmount > 0) this.discountLabel.text = -this.cf.stringFromNumber(i.discountAmount) as any;
                else this.discountLabel.text = null;
            }
            if (this.discountTextField) this.discountTextField.text = i.discountFormat;
            if (this.totalLabel) this.totalLabel.text = this.cf.stringFromNumber(i.baseAmount);
            if (this.totalTextField) this.totalTextField.text = this.cf.stringFromNumber(i.baseAmount);

            this.updateLinkUI();
        }
    }

    // A dropdown reads its value, or its placeholder in the muted colour when it has none.
    protected setDropdownTitle(button:MUIButton, value:string, placeholder:string){
        if (button == null) return;
        let empty = value == null || value.length == 0;
        button.title = empty ? placeholder : value;
        this.toggleStyle(button, "empty", empty);
    }

    protected toggleStyle(view:MUIView, style:string, on:boolean){
        if (view == null) return;
        if (on) MUICoreLayerAddStyle(view.layer, style); else MUICoreLayerRemoveStyle(view.layer, style);
    }

    // The concept column and the VAT as the line stands. Under review, or while the line has no
    // product or concept item to identify it, the product field reads what is linked (red and
    // empty while nothing is) with the printed text in red under it (only when it says more than
    // the name); otherwise the concept reads as text with the "i" after it. The VAT dropdown reads
    // MISSING VAT in red while the line has none.
    protected updateLinkUI(){
        let linked = this.product != null || this.conceptItem != null;
        let name = this.product != null ? this.product.productNameDescription() : (this.conceptItem != null ? this.conceptItem.name : null);
        let review = this.underReview || !linked;
        if (this.conceptTextRow) this.conceptTextRow.hidden = review;
        if (this.productTextField) this.productTextField.hidden = !review;
        if (this.printedRow) this.printedRow.hidden = !review;
        if (this.line != null) {
            if (this.productTextField) this.productTextField.text = name;
            if (this.productLabel) this.productLabel.text = this.conceptString();
            if (this.conceptLabel) {
                let printed = (this.conceptString() ?? "").trim().toLowerCase();
                this.conceptLabel.hidden = name != null && printed == name.trim().toLowerCase();
            }
        }
        this.toggleStyle(this.productTextField, "unlinked", !linked);
        this.toggleStyle(this.conceptLabel, "unlinked", !linked);
        this.updateTaxUI();
    }

    // The VAT dropdown: the tax, or MISSING VAT in red on a line without one (TAX on the new line).
    protected updateTaxUI(){
        let missing = this.vat == null && this.strictVat;
        this.setDropdownTitle(this.taxDropDown, this.vat?.name, missing ? MIOLocalizeString("MISSING_VAT", "MISSING VAT") : MIOLocalizeString("TAX", "TAX"));
        this.toggleStyle(this.taxDropDown, "missing", missing);
    }

    // Links a line of the document to the product or concept item picked in its product field: the
    // amounts stay as they are, the VAT comes from the product when the line has none.
    protected linkLine(item:CoreItemList){
        let line = this.line as InvoiceLine;
        if (!(line instanceof InvoiceLine)) return;
        if (item.product != null) {
            this.product = item.product;
            this.conceptItem = null;
            if (this.vat == null) this.vat = this.taxForProduct(item.product);
        }
        else if (item.conceptItem != null) {
            this.product = null;
            this.conceptItem = item.conceptItem;
        }
        else return;

        this.recalculateTaxes();
        line.product = this.product;
        line.conceptItem = this.conceptItem;
        this.updateLine(line);
        DBHelper.saveMainContext();

        this.updateLinkUI();
        this.delegate?.lineDidUpdate(line);
    }

    /// The VAT a product brings to a line of this kind of document, none by default.
    protected taxForProduct(product:Product) : Tax { return null; }

    // The taxes from the base amount and the VAT as they stand.
    protected recalculateTaxes(){
        this.vat_factor = this.vat != null ? this.vat.taxQuantity : 0;
        this.vat_amount = this.base_amount * this.vat_factor;
        this.legal_tax_amount = this.base_amount * this.legal_tax_factor;
        this.total_amount = this.base_amount + this.vat_amount - this.legal_tax_amount;
    }

    private searchTimer:MIOTimer = null;
    private searchString:string = null;
    protected setupProductTextField(textField:MUITextField, target:any, completion:any){
        if (textField == null) return;        
        textField.setOnChangeText(this, function(control:MUITextField, value:string){
            this.searchString = value;
            if (this.searchTimer != null) this.searchTimer.invalidate();
            this.searchTimer = MIOTimer.scheduledTimerWithTimeInterval(500, false, this, function(timer:MIOTimer){
                this.searchProductTimerFired(textField, this.searchString, target, completion);
                this.searchTimer = null;
            });
        });

        textField.setOnDidEndEditing(this, function(control:MUITextField, value:string){
            // AppHelper.sharedInstance().dismissSelectStockProductViewController();
        });
    }

    protected searchProductTimerFired(textField:MUITextField, value:string, target, completion){        
        AppHelper.sharedInstance().showSelectCoreItemListViewControllerFromView(textField, value, null, target, completion, this, null, this.updateResults);
    }

    private productSearchCount = 0;
    protected updateResults(controller:SelectEntityViewController, count:number){
        this.productSearchCount = count;
    }

    protected itemDidSelect(controller:SelectEntityViewController, item:CoreItemList){
        if (item == null) return;
        // on a line of the document the pick links the line, the amounts stay
        if (this.line != null) { this.linkLine(item); return; }

        this.reset();

        if (item.product != null) {
            this.productDidSelect( controller, item.product, null);
        }
        else if (item.conceptItem != null) {
            this.conceptItem = item.conceptItem;
            this.productTextField.text = item.name;
        }

        this.quantityTextField.becomeFirstResponder();
    }

    protected productDidSelect( controller:SelectEntityViewController, product:Product, supplierProduct?:SupplierProduct ) {
        this.product = product;
        this.productTextField.text = this.product.productNameDescription();
    }

    protected reset(){
        this.productSearchCount = 0;
        this.lockStatus = LockedStatus.none;

        this.product = null;
        this.conceptItem = null;
        // this.productContainerMeasureType = MeasureUnitType.None;
        // this.productContainerQuantity = 0;
        // this.tax = null;
        // this.tax_factor = 0;
        this.applyDefaultLegalTax();
        this.classification = null;
        this.setDropdownTitle(this.classificationDropdown, null, MIOLocalizeString("CLASSIFICATION", "CLASSIFICATION"));
        this.toggleStyle(this.productTextField, "unlinked", false);
        this.updateTaxUI();
        this.quantity = 0;
        this.price_amount = 0;
        this.base_amount = 0;
        this.discount_format = null;
        this.discount_amount = 0;
        this.vat_amount = 0;
        this.legal_tax_amount = 0;
        this.total_amount = 0;
    
        if (this.productTextField != null) this.productTextField.text = null;        

        // if (this.inputFormatDropdown != null) {
        //     this.inputFormatDropdown.title = null;
        //     this.inputFormatDropdown.enabled = false;            
        // }        

        if (this.quantityTextField != null) {
            this.quantityTextField.text = null;
            // this.quantityTextField.enabled = false;
        }        

        // if (this.productQuantityLabel) this.productQuantityLabel.text = null;

        if (this.unitPriceTextField != null) {
            this.unitPriceTextField.text = null;
            // this.unitPriceTextField.enabled = false;
        }

        // if (this.productPriceTextField != null) {
        //     this.productPriceTextField.text = null;
        //     this.productPriceTextField.enabled = false;
        // }

        if (this.discountLabel) this.discountLabel.text = null;
        if (this.discountTextField != null) {
            this.discountTextField.text = null;
            // this.discountFormatTextField.enabled = false;
        }

        // if (this.discountAmountLabel) this.discountAmountLabel.text = null;

        if (this.totalTextField != null) {
            this.totalTextField.text = null;
            // this.totalTextField.enabled = false;
        }
    }

    get entityName() : string { return "InvoiceLine"; }

    enterDidPress(){
        if (this.line != null) return;
        if (this.invoice == null) return;
        if (this.productTextField.text.length == 0) return;
        if (this.quantity == 0 || this.quantity == null) return;

        if (this.vat == null) {
            AppHelper.showErrorMessage(null, MIOLocalizeString("VAT_MISSING", "VAT_MISSING"), MIOLocalizeString( "THE_VAT_IS_MISSING._PLEASE_SELECT_A_VAT.", "THE_VAT_IS_MISSING._PLEASE_SELECT_A_VAT."));
            return;
        }
                        
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let line = MIOEntityDescription.insertNewObjectForEntityForName( this.entityName, ad.managedObjectContext ) as InvoiceLine;

        line.document = this.invoice;
        line.date = new Date();
        line.product = this.product;
        line.conceptItem = this.conceptItem;
        line.concept = this.product != null ? this.product.name : this.productTextField.text;

        if (this.productSearchCount == 0 && this.product == null && this.conceptItem == null) {
            let ci = MIOEntityDescription.insertNewObjectForEntityForName( "ConceptItem", ad.managedObjectContext ) as ConceptItem;
            ci.name = this.productTextField.text;
            line.conceptItem = ci;
        } 

        this.updateLine(line);

        DBHelper.saveMainContext();

        this.delegate.lineDidInserted(line);
        
        this.reset();     

        this.productTextField.becomeFirstResponder();
    }


    protected setupTaxDropdown(button:MUIButton){
        if (button == null) return;
        button.setAction(this, function(){
            AppHelper.sharedInstance().showSelectTaxViewControllerFromView(button, null, null, this, function(this:InvoiceLineCell, controller:SelectViewController, tax:Tax){
                this.vat = tax;
                this.updateTaxUI();
                this.vat_factor = tax != null ? tax.taxQuantity : 0;
                this.recalculate();
            });
        });
    }

    protected setupLegalTaxDropdown(button:MUIButton){
        if (button == null) return;
        button.setAction(this, function(){
            AppHelper.sharedInstance().showSelectLegalEntityTaxViewControllerFromView(button, null, this, function(this:InvoiceLineCell, controller:SelectViewController, tax:LegalTax){
                this.legal_tax = tax;
                this.setDropdownTitle(this.legalTaxDropDown, tax?.name, this.strictVat ? MIOLocalizeString("NONE", "NONE") : MIOLocalizeString("BUSINESS_TAX", "BUSINESS TAX"));
                this.legal_tax_factor = tax != null ? tax.value : 0;
                this.recalculate();
            });
        });
    }

    protected setupClassificationDropdown(button:MUIButton){
        if (button == null) return;
        button.setAction(this, function(this:InvoiceLineCell){
            AppHelper.sharedInstance().showCustomSelectViewControllerFromView(button, "DBDocumentLineClassification", "title", null, this, function(this:InvoiceLineCell, controller:SelectViewController, classification:DBDocumentLineClassification){
                this.classification = classification;
                this.setDropdownTitle(this.classificationDropdown, classification?.title, this.strictVat ? MIOLocalizeString("CHOOSE", "CHOOSE") : MIOLocalizeString("CLASSIFICATION", "CLASSIFICATION"));
                if (this.line != null) {
                    this.line.lineClassification = classification;
                    DBHelper.saveMainContext();
                }
            }, true);
        });
    }


    protected setupQuantityTextField(textField:MUITextField){
        this.setupTextField(textField, this.nf, (value:string) => {
            this.quantity = value.length > 0 ? this.nf.numberFromString(value) : 0;
            this.recalculate();
        });
    }

    protected setupUnitPriceTextField(textField:MUITextField){
        this.setupTextField(textField, this.cf, (value:string) => {
            // Clear price lock first
            this.lockStatus &= ~LockedStatus.price;
            if (value.length > 0) {
                this.price_amount = this.cf.numberFromString(value);
                this.lockStatus |= LockedStatus.price;
            } else {
                // User cleared the field - reset value and keep unlocked
                this.price_amount = 0;
            }
            this.recalculate();
        });
    }

    protected setupDiscountTextField(textField:MUITextField){
        this.setupTextField(textField, this.pnf, (value:string) => {
            // Clear discount lock first
            this.lockStatus &= ~LockedStatus.discount;
            if (value.trim().length == 0) {
                // User cleared the field - reset value and keep unlocked
                this.discount_format = null;
                this.discount_amount = 0;
            } else {
                this.discount_format = value.trim();
                this.lockStatus |= LockedStatus.discount;
                // Discount amount will be calculated in recalculate()
            }
            this.recalculate();
        });
    }

    protected setupTotalTextField(textField:MUITextField){
        this.setupTextField(textField, this.cf, (value:string) => {
            // Clear total lock first
            this.lockStatus &= ~LockedStatus.total;
            if (value.length > 0) {
                this.base_amount = this.cf.numberFromString(value);
                this.lockStatus |= LockedStatus.total;
            } else {
                // User cleared the field - reset value and keep unlocked
                this.base_amount = 0;
            }
            this.recalculate();
        });
    }

    protected setupTextField(textField:MUITextField, formatter:MIOFormatter, onChangeText:any){
        if (textField == null) return;
        textField.formatter = formatter;
        textField.setOnChangeText(this, function(this:InvoiceLineCell, control:MUITextField, value:string){
            onChangeText(value);
        });

        textField.setOnBeginEditing(this, function(control:MUITextField, value:string){
            control.selectAll(null);
        });

        textField.setOnEnterPress(this, function(control:MUITextField){
            control.resignFirstResponder();
            this.enterDidPress()
        });
    }

    private recalculate() {
        // Calculate values based on lock status
        [this.price_amount, this.discount_amount, this.base_amount] = InvoiceLineCell.calculateValues(
            this.quantity,
            this.price_amount,
            this.discount_format,
            this.base_amount,
            this.lockStatus
        );

        // Always recalculate tax amounts
        this.vat_amount = this.base_amount * this.vat_factor;
        this.legal_tax_amount = this.base_amount * this.legal_tax_factor;
        this.total_amount = this.base_amount + this.vat_amount - this.legal_tax_amount;

        // Update all UI fields
        this.updateAllUI();

        // Update line and notify delegate
        this.updateLine(this.line as InvoiceLine);
        if (this.line != null) this.delegate?.lineDidUpdate(this.line as InvoiceLine);
    }

    /**
     * Calculate values based on lock status.
     * Formula: base_amount = (quantity * price) - discount
     *
     * Lock status determines what to calculate:
     * - none: Calculate total from quantity * price - discount
     * - price locked: Calculate total from quantity * price - discount
     * - total locked: Back-calculate price or discount
     * - price + total locked: Calculate discount
     */
    static calculateValues(
        quantity: number,
        price: number,
        discountFormat: string|null,
        total: number,
        lockStatus: LockedStatus
    ): [number, number, number] { // Returns [price, discount, total]

        let p = price;
        let d = 0;
        let t = total;

        // Calculate discount from format if provided
        const calculateDiscountFromFormat = (baseAmount: number): number => {
            if (discountFormat == null || discountFormat.length == 0) return 0;
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            if (discountFormat.hasSuffix("%") == false) {
                return ad.percentNumberFormatter.numberFromString(discountFormat);
            } else {
                let v = ad.percentNumberFormatter.numberFromString(discountFormat);
                return (v * baseAmount) / 100;
            }
        };

        if (lockStatus == LockedStatus.none) {
            // No locks: calculate total from quantity * price
            let grossTotal = quantity * price;
            d = calculateDiscountFromFormat(grossTotal);
            t = grossTotal - d;
        }
        else if (lockStatus == LockedStatus.price) {
            // Price is locked: calculate total from quantity * price
            let grossTotal = quantity * price;
            d = calculateDiscountFromFormat(grossTotal);
            t = grossTotal - d;
        }
        else if (lockStatus == LockedStatus.discount) {
            // Discount is locked: calculate total with the discount
            let grossTotal = quantity * price;
            d = calculateDiscountFromFormat(grossTotal);
            t = grossTotal - d;
        }
        else if (lockStatus == LockedStatus.total) {
            // Total is locked: back-calculate price (assuming no discount format)
            if (quantity > 0) {
                d = calculateDiscountFromFormat(t);
                p = (t + d) / quantity;
            }
        }
        else if (lockStatus == (LockedStatus.price | LockedStatus.total)) {
            // Price and total are locked: calculate discount
            d = (quantity * p) - t;
        }
        else if (lockStatus == (LockedStatus.discount | LockedStatus.total)) {
            // Discount and total are locked: calculate price
            if (quantity > 0) {
                d = calculateDiscountFromFormat(t);
                p = (t + d) / quantity;
            }
        }
        else if (lockStatus == (LockedStatus.price | LockedStatus.discount)) {
            // Price and discount are locked: calculate total
            let grossTotal = quantity * p;
            d = calculateDiscountFromFormat(grossTotal);
            t = grossTotal - d;
        }

        return [p, d, t];
    }

    private updateAllUI() {
        // Only update fields that are NOT locked (user didn't explicitly set them)
        if ((this.lockStatus & LockedStatus.price) == 0) this.updatePriceUI();
        if ((this.lockStatus & LockedStatus.discount) == 0) this.updateDiscountUI();        
        if ((this.lockStatus & LockedStatus.total) == 0) this.updateTotalUI();
    }

    private updateQuantityUI() {
        if (this.quantityTextField) this.quantityTextField.text = this.nf.stringFromNumber(this.quantity);
        if (this.quantityLabel) this.quantityLabel.text = this.nf.stringFromNumber(this.quantity);
    }

    private updatePriceUI() {
        if (this.unitPriceTextField) this.unitPriceTextField.text = this.cf.stringFromNumber(this.price_amount);
        if (this.unitPriceLabel) this.unitPriceLabel.text = this.cf.stringFromNumber(this.price_amount);
    }

    private updateDiscountUI() {
        if (this.discountTextField) this.discountTextField.text = this.cf.stringFromNumber(this.discount_amount);
        if (this.discountLabel) this.discountLabel.text = this.cf.stringFromNumber(this.discount_amount);
    }

    private updateTotalUI() {
        if (this.totalTextField) this.totalTextField.text = this.cf.stringFromNumber(this.base_amount);
        if (this.totalLabel) this.totalLabel.text = this.cf.stringFromNumber(this.base_amount);
    }

    private updateLine(line:InvoiceLine) {

        if (line == null) return;

        line.quantity = this.quantity;
        line.priceAmount = this.price_amount;
        line.vat = this.vat;
        line.vatAmount = this.vat_amount;
        line.vatFactor = this.vat_factor;
        line.legalTax = this.legal_tax;
        line.legalTaxAmount = this.legal_tax_amount;
        line.legalTaxFactor = this.legal_tax_factor;
        line.lineClassification = this.classification;
        line.baseAmount = this.base_amount;
        line.discountFormat = this.discount_format;
        line.discountAmount = this.discount_amount;
        line.totalAmount = this.total_amount;
    }

    protected showInfoAlert(){
        let sections = this.invoice.lines.allObjects.filter( l => l instanceof DBDocumentTitleLine ) as DBDocumentTitleLine[];

        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString ("INFO", "INFO" ), MIOLocalizeString("EXTRA_INFO", "EXTRA INFO"), MUIAlertViewStyle.Default );
        
        avc.addTextFieldWithConfigurationHandler( this, function( this:InvoiceLineCell, control:MUITextField ) {
            control.text = this.line.concept;
        });

        avc.addTextFieldWithConfigurationHandler( this, function( this:InvoiceLineCell, control:MUITextField ) {
            control.text = this.line.comments;
        });

        avc.addTextFieldWithConfigurationHandler( this, function( this:InvoiceLineCell, control:MUITextField ) {
            control.text = this.line.orderIndex.toString();
        });

        if (this.line instanceof DBDocumentTitleLine == false) {
            avc.addComboBoxWithConfigurationHandler( this, function( this:InvoiceLineCell, control:MUIComboBox ) {
                control.removeAllItems();
                control.addItem(MIOLocalizeString("MOVE_TO_SECTION", "MOVE_TO_SECTION"), -2);
                control.addItem(MIOLocalizeString("REMOVE_FROM_SECTION", "REMOVE_FROM_SECTION"), -1);
                for (let i = 0; i < sections.length; i++){
                    control.addItem(sections[i].concept, i);
                }
            });
        }

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK"), MUIAlertActionStyle.Default, this, function(this:InvoiceLineCell){
            let concept = avc.textFields[0].text.trim();
            if ( concept.lenght == 0 ) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE CONCEPT FIELD CAN'T BE NULL OR EMPTY", "THE CONCEPT FIELD CAN'T BE NULL OR EMPTY"));
                return;
            }

            let comments = avc.textFields[1].text.trim();

            this.line.concept = concept;
            this.line.comments = comments.length == 0 ? null : comments;
            
            if (this.line instanceof DBDocumentTitleLine == false) {
                let sectionIndex = avc.comboBoxes[0].getSelectedItem();
                if (sectionIndex > -2) {
                    this.line.parent = sectionIndex == -1 ? null : sections[sectionIndex];
                }
            }

            if (this.conceptLabel) this.conceptLabel.text = this.conceptString();
            if (this.productLabel) this.productLabel.text = this.conceptString();
            this.line.orderIndex = parseInt(avc.textFields[2].text);
            this.updateLinkUI();
            DBHelper.saveMainContext();            
        }));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private conceptString() : string
    {
        return this.line.concept + (this.line.comments != null ? " " + this.line.comments : "" );
    }
    
    private addButtonDidPress(){
        // a line typed in the new line: + adds it, as Enter does
        if (this.line == null && (this.productTextField?.text ?? "").length > 0) { this.enterDidPress(); return; }

        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString ("ADD", "ADD" ), MIOLocalizeString("ADD_NEW_SECTION", "ADD_NEW_SECTION"), MUIAlertViewStyle.Default );        

        avc.addTextFieldWithConfigurationHandler( this, function( this:InvoiceLineCell, control:MUITextField ) {
            control.text = "";
        });

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "ADD", "ADD"), MUIAlertActionStyle.Default, this, function(this:InvoiceLineCell){
            let sectionName = avc.textFields[0].text.trim();
            if ( sectionName.length == 0 ) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE SECTION NAME CAN'T BE NULL OR EMPTY", "THE SECTION NAME CAN'T BE NULL OR EMPTY"));
                return;
            }

            this.addSectionLine(sectionName);
        }));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private addSectionLine(sectionName:string){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let section = MIOEntityDescription.insertNewObjectForEntityForName( "DBDocumentTitleLine", ad.managedObjectContext ) as DBDocumentTitleLine;
        section.concept = sectionName;
        section.document = this.invoice;
        section.date = MIODateNow();

        this.delegate?.lineSectionDidInsert(section);

        DBHelper.saveMainContext();
    }
    
}